import React, { useState } from "react";
import { FaChartColumn, FaUsers, FaLayerGroup, FaHandshake, FaLightbulb, FaBullseye } from "react-icons/fa6";
import { Container, Badge, GradientButton, IconTile } from "./ui";

// Right-side artwork (laptop, plant, floating icons). Put the file at public/img/who-we-are.jpg
// (or change the path). Until it exists, the built-in icon illustration is shown instead.
const sectionImg = "/img/who-we-are.jpg";

// Fades all four edges of the image to transparent (two linear masks intersected),
// so the photo's own background never shows as a box against the section.
const fadeX = "linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 88%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const points = [
  { icon: FaChartColumn, tone: "blue", title: "Business-first thinking", text: "We align technology with your real business goals." },
  { icon: FaUsers, tone: "pink", title: "User-centered design", text: "We create experiences people love to use." },
  { icon: FaLayerGroup, tone: "green", title: "Scalable development", text: "We build solutions that grow with your business." },
  { icon: FaHandshake, tone: "orange", title: "Transparent collaboration", text: "We keep you involved at every step." },
];

const orbit = [
  { icon: FaLightbulb, cls: "left-[44%] top-0 text-violet-600 bg-violet-50" },
  { icon: FaBullseye, cls: "right-[6%] top-[16%] text-emerald-500 bg-emerald-50" },
  { icon: FaUsers, cls: "right-0 top-[52%] text-orange-500 bg-orange-50" },
  { icon: FaChartColumn, cls: "left-[4%] top-[22%] text-blue-600 bg-blue-50" },
];

// Shown only while public/img/who-we-are.jpg is missing.
const FallbackArt = () => (
  <div className="relative mx-auto aspect-square w-full max-w-[520px]">
    <div className="absolute inset-[10%] rounded-full border-2 border-dashed border-blue-100" />
    <div className="absolute inset-[22%] rounded-full bg-gradient-to-br from-blue-100 to-violet-100" />
    {orbit.map(({ icon: Icon, cls }, i) => (
      <span
        key={i}
        className={`absolute z-10 inline-flex h-[64px] w-[64px] animate-float items-center justify-center rounded-2xl text-[26px] shadow-card ring-4 ring-white ${cls}`}
        style={{ animationDelay: `${i * 0.6}s` }}
      >
        <Icon />
      </span>
    ))}
  </div>
);

const WhoWeAre = () => {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <section id="about" className="relative overflow-hidden bg-white py-[64px] lg:py-[68px]">
      <Container className="grid items-center gap-[40px] lg:grid-cols-[1.08fr_1fr] lg:gap-[32px]">
        <div>
          <Badge>Who we are</Badge>
          <h2 className="mt-[20px] text-[30px] font-bold leading-[1.18] tracking-tight text-ink sm:text-[36px] xl:text-[40px]">
            More Than Developers.
            <br />
            <span className="text-gradient">Your Digital Growth Partners.</span>
          </h2>
          <p className="mt-[12px] max-w-[600px] text-[15px] leading-[1.65] text-slate-600 sm:text-[16px]">
            We believe great digital products begin with understanding people and business challenges. Our team brings
            design, technology, and strategy together to build solutions that are practical today and ready for tomorrow.
          </p>

          <div className="mt-[16px] grid max-w-[620px] gap-[14px] sm:grid-cols-2">
            {points.map((p) => (
              <div
                key={p.title}
                className="flex items-start gap-[14px] rounded-[16px] border-[1px] border-solid border-[#EDF0F7] bg-white px-[16px] py-[18px] shadow-[0_8px_24px_-16px_rgba(40,60,140,0.18)]"
              >
                <IconTile icon={p.icon} tone={p.tone} />
                <div>
                  <h3 className="text-[14.5px] font-semibold leading-snug text-ink">{p.title}</h3>
                  <p className="mt-[6px] text-[13.5px] leading-[1.5] text-slate-500">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          <GradientButton href="/about-us" className="mt-[22px]  px-[30px]">
            Discover Our Story
          </GradientButton>
        </div>

        <div className="relative">
          {imgFailed ? (
            <FallbackArt />
          ) : (
            <img
              src={sectionImg}
              alt="Laptop with code, plant and icons for strategy, design and teamwork"
              onError={() => setImgFailed(true)}
              className="mx-auto h-auto w-full max-w-[640px] object-contain lg:ml-auto lg:mr-0"
              style={edgeFade}
            />
          )}
        </div>
      </Container>
    </section>
  );
};

export default WhoWeAre;
